import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { format } from 'date-fns';
import { useLexware } from '../../context/LexwareContext';

const { FiX, FiMail, FiCalendar, FiActivity, FiSearch, FiFilter, FiUserCheck, FiUserX } = FiIcons;

function ChurnListModal({ title, customers, onClose, onSelectCustomer }) {
  const { data } = useLexware();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUnprocessed, setFilterUnprocessed] = useState(false);
  const [minDays, setMinDays] = useState('');

  const safeCustomers = customers || [];

  const isCritical = title?.toLowerCase().includes('kritisch') || title?.toLowerCase().includes('inaktiv');
  const isWarning = title?.toLowerCase().includes('verzögerung') || title?.toLowerCase().includes('warnung');
  const isObservation = title?.toLowerCase().includes('beobachtung');

  const colorTheme = isCritical 
    ? { bg: 'bg-red-50', border: 'border-red-100', text: 'text-red-700', subtext: 'text-red-400' }
    : isWarning
    ? { bg: 'bg-orange-50', border: 'border-orange-100', text: 'text-orange-700', subtext: 'text-orange-400' }
    : isObservation
    ? { bg: 'bg-yellow-50', border: 'border-yellow-100', text: 'text-yellow-700', subtext: 'text-yellow-600' }
    : { bg: 'bg-orange-50', border: 'border-orange-100', text: 'text-orange-700', subtext: 'text-orange-400' };

  const getCustomerStatus = (customerId) => {
    const pendingTasks = (data?.tasks || []).filter(
      t => t.customerId === customerId && t.status !== 'COMPLETED'
    );
    
    if (pendingTasks.length > 0) {
      pendingTasks.sort((a, b) => new Date(a.dueDate || a.createdAt) - new Date(b.dueDate || b.createdAt));
      return { type: 'task', data: pendingTasks[0] };
    }

    const customerLogs = (data?.logs || []).filter(
      l => l.metadata?.customerId === customerId
    );
    
    if (customerLogs.length > 0) {
      customerLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      return { type: 'activity', data: customerLogs[0] };
    }

    return null;
  };

  const filteredCustomers = useMemo(() => {
    return safeCustomers.filter(customer => {
      const matchesSearch = !searchTerm || 
        customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.id?.toString().includes(searchTerm);
      
      if (!matchesSearch) return false;

      if (filterUnprocessed) {
        const status = getCustomerStatus(customer.id);
        if (status?.type === 'task') return false;
      }

      if (minDays) {
        const days = customer.overdueDays || customer.daysOverdue || 0;
        if (days < parseInt(minDays)) return false;
      }

      return true;
    });
  }, [safeCustomers, searchTerm, filterUnprocessed, minDays, data.tasks, data.logs]);

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    if (onClose) onClose();
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, pointerEvents: 'none' }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" 
      onClick={handleClose}
    >
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }} 
        animate={{ opacity: 1, scale: 1, y: 0 }} 
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-white rounded-[2rem] shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden relative" 
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`p-8 border-b flex justify-between items-center ${colorTheme.bg} ${colorTheme.border}`}>
          <div>
            <h2 className={`text-3xl font-black ${colorTheme.text}`}>{title}</h2>
            <div className="flex items-center gap-4 mt-1">
              <p className={`text-sm font-bold uppercase tracking-wider ${colorTheme.subtext}`}>
                {safeCustomers.length} Kunden gesamt
              </p>
              {filteredCustomers.length !== safeCustomers.length && (
                <p className="text-xs font-black bg-white px-2 py-0.5 rounded-full text-blue-600 shadow-sm border border-blue-50">
                  {filteredCustomers.length} gefiltert
                </p>
              )}
            </div>
          </div>
          <button 
            type="button"
            onClick={handleClose} 
            className="p-4 bg-white rounded-full hover:bg-gray-50 transition-all shadow-sm cursor-pointer z-[110]"
          >
            <SafeIcon icon={FiX} className="text-xl text-gray-400" />
          </button>
        </div>

        {/* Filter Bar */}
        <div className="px-8 py-5 bg-white border-b border-gray-100 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[240px] relative">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text"
              placeholder="Nach Name oder KD-NR suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:border-blue-500 focus:bg-white transition-all font-bold text-sm"
            />
          </div>

          <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-2xl border border-gray-100">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Min. Tage:</label>
            <input 
              type="number"
              value={minDays}
              onChange={(e) => setMinDays(e.target.value)}
              placeholder="0"
              className="w-16 bg-transparent outline-none font-black text-blue-600 border-b-2 border-transparent focus:border-blue-500 text-center"
            />
          </div>

          <button 
            onClick={() => setFilterUnprocessed(!filterUnprocessed)}
            className={`flex items-center gap-2 px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-wider transition-all border-2 ${
              filterUnprocessed 
              ? 'bg-orange-500 text-white border-orange-500 shadow-lg shadow-orange-200' 
              : 'bg-white text-gray-500 border-gray-100 hover:border-gray-200'
            }`}
          >
            <SafeIcon icon={filterUnprocessed ? FiUserX : FiUserCheck} className="text-sm" />
            Nur Unbearbeitet
          </button>

          {(searchTerm || filterUnprocessed || minDays) && (
            <button 
              onClick={() => {
                setSearchTerm('');
                setFilterUnprocessed(false);
                setMinDays('');
              }}
              className="text-xs font-black text-gray-400 hover:text-red-500 transition-colors uppercase tracking-widest px-2"
            >
              Reset
            </button>
          )}
        </div>

        {/* List Header */}
        <div className="grid grid-cols-12 gap-4 px-8 py-4 bg-gray-50/50 border-b border-gray-100 text-[10px] font-black text-gray-400 uppercase tracking-widest">
          <div className="col-span-4">Kunde</div>
          <div className="col-span-3">Status / Aktivität</div>
          <div className="col-span-2 text-right">Letzte Order</div>
          <div className="col-span-1 text-center">Tage</div>
          <div className="col-span-2 text-right">Aktion</div>
        </div>

        {/* List Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
          {filteredCustomers.length > 0 ? filteredCustomers.map(customer => {
            const daysOverdue = customer.overdueDays || customer.daysOverdue || 0;
            const lastOrderDate = customer.lastOrderDate || (customer.daysSinceOrder ? new Date(Date.now() - customer.daysSinceOrder * 24 * 60 * 60 * 1000) : null);
            const status = getCustomerStatus(customer.id);
            
            return (
              <motion.div 
                layout
                key={customer.id} 
                onClick={(e) => {
                  e.stopPropagation();
                  if (onSelectCustomer) onSelectCustomer(customer);
                }}
                className="grid grid-cols-12 gap-4 items-center p-4 rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-lg hover:bg-blue-50/10 transition-all group cursor-pointer"
              >
                <div className="col-span-4 flex items-center gap-4 overflow-hidden">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-black border flex-shrink-0 ${
                    isCritical ? 'bg-red-50 text-red-600 border-red-100' : 
                    isWarning ? 'bg-orange-50 text-orange-600 border-orange-100' :
                    'bg-yellow-50 text-yellow-600 border-yellow-100'
                  }`}>
                    {customer.name?.charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-black text-gray-900 text-sm md:text-base leading-tight group-hover:text-blue-600 transition-colors truncate">
                      {customer.name}
                    </h3>
                    <p className="text-[10px] md:text-xs text-gray-400 font-bold mt-0.5 truncate">{customer.id}</p>
                  </div>
                </div>

                <div className="col-span-3 min-w-0">
                  {status?.type === 'task' && (
                    <div className="flex flex-col">
                      <span className="text-[10px] font-black text-orange-500 uppercase flex items-center gap-1">
                         <SafeIcon icon={FiCalendar} /> Anstehend
                      </span>
                      <span className="text-xs font-bold text-gray-800 truncate" title={status.data.title}>
                        {status.data.title}
                      </span>
                    </div>
                  )}
                  {status?.type === 'activity' && (
                    <div className="flex flex-col">
                      <span className="text-[10px] font-black text-gray-400 uppercase flex items-center gap-1">
                         <SafeIcon icon={FiActivity} /> Letzte Aktivität
                      </span>
                      <span className="text-xs font-bold text-gray-600 truncate">
                        {format(new Date(status.data.timestamp), 'dd.MM')} - {status.data.type.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                  {!status && (
                    <span className="text-xs font-bold text-gray-300 italic">Keine Einträge</span>
                  )}
                </div>

                <div className="col-span-2 text-right font-bold text-gray-600 text-xs md:text-sm">
                  {lastOrderDate ? format(new Date(lastOrderDate), 'dd.MM.yyyy') : 'N/A'}
                </div>

                <div className="col-span-1 flex justify-center">
                  <span className={`px-2 md:px-3 py-1 rounded-lg text-xs font-black uppercase tracking-wider text-center shadow-sm border ${
                    isCritical ? 'bg-red-50 text-red-600 border-red-100' :
                    isWarning ? 'bg-orange-50 text-orange-600 border-orange-100' :
                    'bg-yellow-50 text-yellow-600 border-yellow-100'
                  }`}>
                    {daysOverdue}<br className="hidden md:block"/> <span className="md:hidden">T</span><span className="hidden md:inline">Tage</span>
                  </span>
                </div>

                <div className="col-span-2 flex justify-end">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onSelectCustomer) onSelectCustomer(customer);
                    }}
                    className="px-3 md:px-4 py-2 bg-gray-900 text-white rounded-xl text-xs font-bold hover:bg-gray-800 transition-all flex items-center gap-2 shadow-lg shadow-gray-200"
                  >
                    <SafeIcon icon={FiMail} /> <span className="hidden md:inline">Details</span>
                  </button>
                </div>
              </motion.div>
            );
          }) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 py-20">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <SafeIcon icon={FiFilter} className="text-2xl" />
              </div>
              <p className="font-bold">Keine Kunden gefunden</p>
              <p className="text-xs mt-1">Versuche die Filter anzupassen</p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default ChurnListModal;